import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Full-screen profile editor for a single user.
 *
 * Mounted on both surfaces:
 *  - Customer side:   /users/:userId             (CustomerHome route)
 *  - Admin side:      /customers/:cid/users/:userId
 *
 * The parent route resolves the right ``apiBase`` (a base path
 * ending in ``/users``) and passes it in. Everything else here is
 * shared.
 *
 * Layout: three big visual blocks instead of the legacy modal —
 *   רואה  — site scope (PR 2 keeps the legacy radio + checkbox UI;
 *           PR 5 will add gate-group scope on top).
 *   יכול  — six permission checkboxes in plain Hebrew.
 *   מקבל  — notification channels editor (legacy ChannelsEditor,
 *           lifted out to live in this page).
 *
 * Footer actions: send password-reset link, resend invite (only
 * before first login), deactivate / reactivate.
 */

const PERMISSION_DESCRIPTIONS = [
  { key: 'can_open_close_gates',         label: 'פתיחה וסגירה ידנית של שערים' },
  { key: 'can_temp_unlock',              label: 'יצירת חלון שחרור נעילה זמני' },
  { key: 'can_edit_gate_settings',       label: 'עריכת הגדרות שערים ולוחות זמנים' },
  { key: 'can_manage_sites_and_groups',  label: 'ניהול אתרים וקבוצות שערים' },
  { key: 'can_manage_users',             label: 'ניהול משתמשים והרשאות' },
  { key: 'can_acknowledge_alerts',       label: 'אישור התראות כטופלו' },
];

const ALERT_TYPE_LABELS = {
  long_open:         'שער שנשאר פתוח יותר מדי זמן',
  fault:             'תקלה בציוד',
  offline:           'הרכיב מאבד תקשורת',
  unauthorized_open: 'מישהו פתח שער מחוץ לשעות',
  close_failed:      'השער לא נסגר לאחר פקודה',
};


function UserProfile({ usersBase, userId, currentUserId, onBack, onChanged }) {
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const channelsRoot = usersBase.replace(/\/users$/, '') + '/channels';
  const templatesEndpoint = usersBase.replace(/\/users$/, '') + '/user-templates';

  const id = userId ?? user?.id;

  const load = () => {
    if (!id) return;
    axios.get(`${usersBase}/${id}`)
      .then(res => setUser(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  };
  useEffect(load, [id, usersBase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(templatesEndpoint)
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => setTemplates([]));
  }, [templatesEndpoint]);

  const flash = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2400);
  };

  if (!user && !error) return <div className="loading">טוען...</div>;
  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← חזרה</button>
        <div style={{
          marginTop: '1rem',
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', padding: '0.6rem 0.85rem',
          borderRadius: '6px', fontSize: '0.85rem',
        }}>{error}</div>
      </div>
    );
  }

  const isSelf = currentUserId === user.id;

  const onPersistDetails = async (payload) => {
    await axios.patch(`${usersBase}/${user.id}`, payload);
    flash('הפרטים נשמרו');
    load();
    onChanged && onChanged();
  };

  const onApplyTemplate = async (templateKey) => {
    try {
      await axios.patch(`${usersBase}/${user.id}`, { template_key: templateKey });
      flash('התבנית הוחלפה');
      load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהחלפת התבנית');
    }
  };

  const onTogglePermission = async (flag, value) => {
    try {
      await axios.patch(`${usersBase}/${user.id}`, {
        permissions: { [flag]: value },
      });
      load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון ההרשאה');
    }
  };

  const onDeactivate = async () => {
    if (!window.confirm(`להשבית את ${user.name}? המשתמש לא יוכל להתחבר עד שיופעל מחדש.`)) return;
    try {
      await axios.post(`${usersBase}/${user.id}/deactivate`);
      flash('המשתמש הושבת');
      load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהשבתה');
    }
  };

  const onReactivate = async () => {
    try {
      await axios.post(`${usersBase}/${user.id}/reactivate`);
      flash('המשתמש הופעל מחדש');
      load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהפעלה מחדש');
    }
  };

  const onSendPasswordReset = async () => {
    try {
      await axios.post(`${usersBase}/${user.id}/send-password-reset`);
      flash('נשלח קישור איפוס סיסמה למייל');
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשליחה');
    }
  };

  const onResendInvite = async () => {
    try {
      await axios.post(`${usersBase}/${user.id}/resend-invite`);
      flash('הזמנה חדשה נשלחה במייל');
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשליחה');
    }
  };

  const statusLabel = !user.is_active
    ? { text: 'מושבת', bg: '#fef2f2', fg: '#b91c1c' }
    : (user.has_logged_in
      ? { text: 'פעיל', bg: '#ecfdf5', fg: '#047857' }
      : { text: 'הוזמן — טרם נכנס', bg: '#fffbeb', fg: '#92400e' });

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← חזרה לרשימה</button>
      </div>

      <ProfileHeader user={user} statusLabel={statusLabel} isSelf={isSelf} />

      <DetailsCard user={user} onSave={onPersistDetails} />

      <TemplateCard
        user={user}
        templates={templates}
        onApply={onApplyTemplate}
        isSelf={isSelf}
      />

      <PermissionsCard
        user={user}
        isSelf={isSelf}
        onToggle={onTogglePermission}
      />

      <SiteScopeCard user={user} usersBase={usersBase} />

      <ChannelsCard
        user={user}
        usersBase={usersBase}
        channelsRoot={channelsRoot}
      />

      <SmsComingSoonCard />

      <FooterActions
        user={user}
        isSelf={isSelf}
        onSendPasswordReset={onSendPasswordReset}
        onResendInvite={onResendInvite}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
      />

      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e', color: '#fff',
          padding: '0.6rem 1.1rem', borderRadius: '6px',
          fontSize: '0.85rem', zIndex: 200,
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}


// ---- Header --------------------------------------------------------------

function ProfileHeader({ user, statusLabel, isSelf }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '1.1rem 1.25rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
          {user.name}
          {isSelf && (
            <span style={{ marginRight: '0.5rem', color: '#9ca3af', fontSize: '0.78rem', fontWeight: 400 }}>
              (אתה)
            </span>
          )}
        </h2>
        <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '0.2rem' }} dir="ltr">
          {user.email}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '999px',
          fontSize: '0.78rem',
          fontWeight: 500,
          background: statusLabel.bg,
          color: statusLabel.fg,
        }}>{statusLabel.text}</span>
        {user.last_login_at && (
          <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>
            כניסה אחרונה: {new Date(user.last_login_at).toLocaleString('he-IL', {
              timeZone: 'Asia/Jerusalem',
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
        )}
      </div>
    </div>
  );
}


// ---- Section card wrapper ------------------------------------------------

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '1.1rem 1.25rem',
      marginBottom: '1rem',
    }}>
      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{title}</h3>
      {subtitle && (
        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.3rem 0 0' }}>
          {subtitle}
        </p>
      )}
      <div style={{ marginTop: '0.85rem' }}>
        {children}
      </div>
    </div>
  );
}


// ---- Details (name + email) ----------------------------------------------

function DetailsCard({ user, onSave }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user.name);
    setEmail(user.email || '');
    setPhone(user.phone || '');
  }, [user.id, user.name, user.email, user.phone]);

  const dirty =
    name !== user.name ||
    email !== (user.email || '') ||
    phone !== (user.phone || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ name, email, phone });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="פרטים"
      subtitle="השם, האימייל ומספר הטלפון. הטלפון משמש לכניסה עם קוד בוואטסאפ."
    >
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#4a5568', marginBottom: '0.25rem' }}>
            שם מלא
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#4a5568', marginBottom: '0.25rem' }}>
            אימייל
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            dir="ltr"
            style={{ ...inputStyle, textAlign: 'left' }}
          />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#4a5568', marginBottom: '0.25rem' }}>
            טלפון (לכניסה בוואטסאפ)
            {user.phone && (
              <span style={{
                marginRight: '0.4rem',
                fontSize: '0.72rem',
                fontWeight: 500,
                color: user.phone_verified ? '#047857' : '#92400e',
              }}>
                {user.phone_verified ? '• מאומת' : '• טרם אומת'}
              </span>
            )}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="05X-XXXXXXX"
            dir="ltr"
            style={{ ...inputStyle, textAlign: 'left' }}
          />
        </div>
      </div>
      {dirty && (
        <div style={{ marginTop: '0.75rem' }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </button>
        </div>
      )}
    </SectionCard>
  );
}


// ---- Template + change-template ------------------------------------------

function TemplateCard({ user, templates, onApply, isSelf }) {
  const [picking, setPicking] = useState(false);

  const currentTemplate = templates.find(t => t.key === user.template_key);
  const visibleTemplates = templates.filter(t => {
    if (isSelf && t.key === 'viewer') return false; // can't demote self
    return true;
  });

  return (
    <SectionCard
      title="תפקיד"
      subtitle="התבנית קובעת את ההרשאות וההתראות. שינוי תבנית מאפס את ההרשאות לערכי ברירת המחדל של התבנית החדשה."
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '0.6rem 0.85rem',
          fontWeight: 500,
          fontSize: '0.92rem',
        }}>
          {currentTemplate?.label || user.template_label || '—'}
          {user.template_customized && (
            <span style={{ marginRight: '0.4rem', color: '#9ca3af', fontSize: '0.78rem', fontWeight: 400 }}>
              • הותאם
            </span>
          )}
        </div>
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          onClick={() => setPicking(p => !p)}
        >
          {picking ? 'סגור' : 'החלף תבנית'}
        </button>
      </div>

      {picking && (
        <div style={{
          marginTop: '0.85rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.6rem',
        }}>
          {visibleTemplates.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                if (window.confirm(`להחליף את התפקיד ל"${t.label}"? פעולה זו תאפס את ההרשאות וההתראות לברירת המחדל של התבנית.`)) {
                  onApply(t.key);
                  setPicking(false);
                }
              }}
              style={{
                textAlign: 'right',
                background: t.key === user.template_key ? '#1a1a2e' : '#fff',
                color: t.key === user.template_key ? '#fff' : '#1a1a2e',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.7rem',
                cursor: 'pointer',
                fontFamily: 'Heebo, sans-serif',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{t.label}</div>
              <div style={{
                fontSize: '0.74rem',
                color: t.key === user.template_key ? '#cbd5e1' : '#6b7280',
                marginTop: '0.25rem',
                lineHeight: 1.4,
              }}>
                {t.description}
              </div>
            </button>
          ))}
        </div>
      )}
    </SectionCard>
  );
}


// ---- Permissions block ---------------------------------------------------

function PermissionsCard({ user, isSelf, onToggle }) {
  const perms = user.permissions || {};
  return (
    <SectionCard
      title="יכול"
      subtitle="הפעולות שהמשתמש מורשה לבצע במערכת. שינוי כאן ישפיע מיד."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {PERMISSION_DESCRIPTIONS.map(p => {
          const checked = !!perms[p.key];
          // Safety mirror of backend: an actor can't strip their own
          // can_manage_users in this UI.
          const disabled = isSelf && p.key === 'can_manage_users';
          return (
            <label
              key={p.key}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.5rem 0.6rem',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={e => onToggle(p.key, e.target.checked)}
              />
              <span style={{ fontSize: '0.88rem' }}>{p.label}</span>
              {disabled && (
                <span style={{ marginInlineStart: 'auto', fontSize: '0.72rem', color: '#9ca3af' }}>
                  לא ניתן להסיר מעצמך
                </span>
              )}
            </label>
          );
        })}
      </div>
    </SectionCard>
  );
}


// ---- Site scope ----------------------------------------------------------

function SiteScopeCard({ user, usersBase }) {
  const [scopeMode, setScopeMode] = useState('all');
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${usersBase}/${user.id}/site-scope`).then(res => {
      const ids = res.data.site_ids || [];
      setAvailable(res.data.available_sites || []);
      setSelected(ids);
      setScopeMode(ids.length === 0 ? 'all' : 'selected');
      setLoaded(true);
      setDirty(false);
    }).catch(() => setLoaded(true));
  }, [user.id, usersBase]);

  // Site scope is only meaningful for viewers in the legacy code; we
  // keep the same behaviour here and hide for non-viewer templates.
  if (user.role !== 'viewer' && user.template_key !== 'shift_manager') {
    return (
      <SectionCard
        title="רואה"
        subtitle="המשתמש רואה את כל האתרים של החברה."
      >
        <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
          תפקידים מנהליים רואים את כל האתרים. הגבלה לאתרים מסוימים זמינה רק לתבניות "צופה" ו"מנהל משמרת".
        </div>
      </SectionCard>
    );
  }

  if (!loaded) return null;

  const toggleSite = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setDirty(true);
  };

  const onModeChange = (mode) => {
    setScopeMode(mode);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = scopeMode === 'all' ? { site_ids: [] } : { site_ids: selected };
      await axios.put(`${usersBase}/${user.id}/site-scope`, payload);
      setDirty(false);
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשמירת ההיקף');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="רואה"
      subtitle="אילו אתרים המשתמש רואה במערכת. אם לא נבחר דבר — רואה את כל האתרים."
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', cursor: 'pointer' }}>
        <input
          type="radio"
          checked={scopeMode === 'all'}
          onChange={() => onModeChange('all')}
        />
        <span style={{ fontSize: '0.9rem' }}>כל האתרים</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
        <input
          type="radio"
          checked={scopeMode === 'selected'}
          onChange={() => onModeChange('selected')}
        />
        <span style={{ fontSize: '0.9rem' }}>אתרים נבחרים בלבד</span>
      </label>
      {scopeMode === 'selected' && (
        <div style={{
          paddingInlineStart: '1.5rem',
          display: 'flex', flexDirection: 'column', gap: '0.3rem',
          maxHeight: '220px', overflowY: 'auto',
        }}>
          {available.map(s => (
            <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={selected.includes(s.id)}
                onChange={() => toggleSite(s.id)}
              />
              {s.name}
            </label>
          ))}
        </div>
      )}
      {dirty && (
        <div style={{ marginTop: '0.75rem' }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'שמור היקף'}
          </button>
        </div>
      )}
    </SectionCard>
  );
}


// ---- Channels ------------------------------------------------------------

function ChannelsCard({ user, usersBase, channelsRoot }) {
  const [channels, setChannels] = useState([]);
  const [alertTypes, setAlertTypes] = useState(Object.keys(ALERT_TYPE_LABELS));
  const [availableSites, setAvailableSites] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [error, setError] = useState(null);

  const load = () => {
    axios.get(`${usersBase}/${user.id}/channels`)
      .then(res => {
        setChannels(res.data.channels || []);
        if (res.data.alert_types) setAlertTypes(res.data.alert_types);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };
  useEffect(load, [user.id, usersBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // PR 4: per-channel site picker needs the list of sites this user
  // can see. Reuses the site-scope endpoint that already returns
  // ``available_sites`` for the user; no new backend route needed.
  useEffect(() => {
    axios.get(`${usersBase}/${user.id}/site-scope`)
      .then(res => setAvailableSites(res.data.available_sites || []))
      .catch(() => setAvailableSites([]));
  }, [user.id, usersBase]);

  const patchChannel = async (id, payload) => {
    setError(null);
    try {
      const res = await axios.patch(`${channelsRoot}/${id}`, payload);
      setChannels(cs => cs.map(c => c.id === id ? res.data : c));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בעדכון הערוץ');
    }
  };

  const deleteChannel = async (id) => {
    if (!window.confirm('למחוק את הערוץ? לא יישלחו אליו עוד התראות.')) return;
    setError(null);
    try {
      await axios.delete(`${channelsRoot}/${id}`);
      setChannels(cs => cs.filter(c => c.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במחיקת הערוץ');
    }
  };

  const createChannel = async () => {
    setError(null);
    if (!newAddress.trim()) {
      setError('יש להזין כתובת אימייל');
      return;
    }
    try {
      const res = await axios.post(`${usersBase}/${user.id}/channels`, {
        address: newAddress.trim(),
        alert_types: alertTypes,
        enabled: true,
      });
      setChannels(cs => [...cs, res.data]);
      setNewAddress('');
      setCreating(false);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת הערוץ');
    }
  };

  if (!loaded) return null;

  return (
    <SectionCard
      title="מקבל התראות במייל"
      subtitle="לאיזה כתובות יישלחו התראות, ולאילו סוגי אירועים. כל ערוץ ניתן להפעלה / כיבוי בנפרד."
    >
      {channels.length === 0 && !creating && (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0' }}>
          אין ערוצים — המשתמש לא יקבל התראות במייל.
        </div>
      )}

      {channels.map(ch => (
        <ChannelRow
          key={ch.id}
          channel={ch}
          alertTypes={alertTypes}
          availableSites={availableSites}
          onChange={(payload) => patchChannel(ch.id, payload)}
          onDelete={() => deleteChannel(ch.id)}
        />
      ))}

      <div style={{ marginTop: '0.6rem' }}>
        {!creating && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setCreating(true)}
          >
            + הוסף כתובת מייל
          </button>
        )}
        {creating && (
          <div style={{
            border: '1px dashed #d1d5db',
            borderRadius: '6px',
            padding: '0.55rem',
            marginTop: '0.4rem',
            display: 'flex',
            gap: '0.4rem',
            alignItems: 'center',
          }}>
            <input
              value={newAddress}
              onChange={e => setNewAddress(e.target.value)}
              placeholder="כתובת אימייל"
              dir="ltr"
              style={{ ...inputStyle, flex: 1, textAlign: 'left' }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); createChannel(); }
                else if (e.key === 'Escape') { setCreating(false); setNewAddress(''); }
              }}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={createChannel}>
              הוסף
            </button>
            <button
              type="button"
              onClick={() => { setCreating(false); setNewAddress(''); }}
              style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontFamily: 'Heebo, sans-serif', fontSize: '0.85rem' }}
            >
              ביטול
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', padding: '0.4rem 0.65rem',
          borderRadius: '5px', marginTop: '0.5rem', fontSize: '0.78rem',
        }}>
          {error}
        </div>
      )}
    </SectionCard>
  );
}


function ChannelRow({ channel, alertTypes, availableSites = [], onChange, onDelete }) {
  const [address, setAddress] = useState(channel.address);
  const [savingTimer, setSavingTimer] = useState(null);

  useEffect(() => { setAddress(channel.address); }, [channel.address]);

  const queueAddressSave = (val) => {
    if (savingTimer) clearTimeout(savingTimer);
    const t = setTimeout(() => {
      if (val !== channel.address) onChange({ address: val });
    }, 600);
    setSavingTimer(t);
  };

  const toggleType = (t) => {
    const has = (channel.alert_types || []).includes(t);
    const next = has
      ? (channel.alert_types || []).filter(x => x !== t)
      : [...(channel.alert_types || []), t];
    onChange({ alert_types: next });
  };

  // Per-channel site filter: ``null``/missing = "every site visible
  // to the user", an array = restricted to those sites. The UI
  // mirrors the radios-then-chips pattern from the "רואה" section so
  // managers don't learn a second UI just for channels.
  const siteScopeMode = (channel.site_ids && channel.site_ids.length > 0) ? 'selected' : 'all';
  const setAllSites = () => {
    if (siteScopeMode !== 'all') onChange({ site_ids: [] });
  };
  const setSelectedSites = () => {
    if (siteScopeMode !== 'selected') {
      // Switch to "selected" with every available site pre-checked,
      // so the manager doesn't see an empty-list state that looks
      // like "nothing".
      onChange({ site_ids: availableSites.map(s => s.id) });
    }
  };
  const toggleSite = (siteId) => {
    const current = channel.site_ids || [];
    const has = current.includes(siteId);
    const next = has ? current.filter(x => x !== siteId) : [...current, siteId];
    onChange({ site_ids: next });
  };

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '0.65rem',
      marginTop: '0.4rem',
      background: channel.enabled ? '#fff' : '#f9fafb',
      opacity: channel.enabled ? 1 : 0.75,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.55rem' }}>
        <input
          value={address}
          onChange={e => { setAddress(e.target.value); queueAddressSave(e.target.value); }}
          onBlur={e => {
            if (savingTimer) clearTimeout(savingTimer);
            if (e.target.value !== channel.address) onChange({ address: e.target.value });
          }}
          dir="ltr"
          style={{ ...inputStyle, flex: 1, textAlign: 'left' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: '#4a5568', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={channel.enabled}
            onChange={e => onChange({ enabled: e.target.checked })}
          />
          פעיל
        </label>
        <button
          type="button"
          onClick={onDelete}
          style={{
            padding: '0.3rem 0.55rem',
            background: '#fef2f2',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            borderRadius: '5px',
            fontSize: '0.75rem',
            cursor: 'pointer',
            fontFamily: 'Heebo, sans-serif',
          }}
        >
          מחק
        </button>
      </div>

      <div style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.4rem 0 0.25rem' }}>
        אילו התראות:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {alertTypes.map(t => {
          const checked = (channel.alert_types || []).includes(t);
          return (
            <label
              key={t}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.25rem 0.65rem',
                border: '1px solid #e5e7eb',
                borderRadius: '999px',
                fontSize: '0.78rem',
                cursor: 'pointer',
                background: checked ? '#1a1a2e' : '#fff',
                color: checked ? '#fff' : '#4a5568',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleType(t)}
                style={{ display: 'none' }}
              />
              {ALERT_TYPE_LABELS[t] || t}
            </label>
          );
        })}
      </div>

      {availableSites.length > 1 && (
        <>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.6rem 0 0.25rem' }}>
            מאיזה אתרים:
          </div>
          <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={siteScopeMode === 'all'}
                onChange={setAllSites}
              />
              כל האתרים
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={siteScopeMode === 'selected'}
                onChange={setSelectedSites}
              />
              אתרים נבחרים בלבד
            </label>
          </div>
          {siteScopeMode === 'selected' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {availableSites.map(s => {
                const checked = (channel.site_ids || []).includes(s.id);
                return (
                  <label
                    key={s.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.22rem 0.65rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '999px',
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      background: checked ? '#1a1a2e' : '#fff',
                      color: checked ? '#fff' : '#4a5568',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSite(s.id)}
                      style={{ display: 'none' }}
                    />
                    {s.name}
                  </label>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ---- SMS placeholder ------------------------------------------------------

/**
 * Visual-only placeholder for the future SMS channel.
 *
 * Sits below the email channels with the same SectionCard chrome
 * + a "בקרוב" badge and a disabled "+ הוסף מספר טלפון" button.
 * Renders the same conceptual UI the email side has so a manager
 * can already see where the SMS channels will land — but stores
 * nothing and doesn't pretend to dispatch. When SMS is wired up
 * for real (separate phase, needs a paid provider account), this
 * component becomes a real ``SmsChannelsCard`` that mirrors
 * ``ChannelsCard``.
 */
function SmsComingSoonCard() {
  const disabledBtn = {
    padding: '0.4rem 0.85rem',
    background: '#f3f4f6',
    color: '#9ca3af',
    border: '1px solid #e5e7eb',
    borderRadius: '5px',
    fontSize: '0.82rem',
    cursor: 'not-allowed',
    fontFamily: 'Heebo, sans-serif',
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '1.1rem 1.25rem',
      marginBottom: '1rem',
      opacity: 0.9,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#6b7280' }}>
          מקבל התראות ב-SMS
        </h3>
        <span style={{
          padding: '0.15rem 0.55rem',
          borderRadius: '999px',
          fontSize: '0.7rem',
          fontWeight: 500,
          background: '#fffbeb',
          color: '#92400e',
          border: '1px solid #fde68a',
        }}>
          בקרוב
        </span>
      </div>
      <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0.35rem 0 0' }}>
        בעתיד תוכל להוסיף כאן מספרי טלפון שיקבלו התראות ב-SMS, עם אותם מסנני התראות ואתרים כמו במייל.
      </p>
      <div style={{ marginTop: '0.85rem' }}>
        <button type="button" style={disabledBtn} disabled>
          + הוסף מספר טלפון
        </button>
      </div>
    </div>
  );
}


// ---- Footer (destructive + invite-related actions) -----------------------

function FooterActions({ user, isSelf, onSendPasswordReset, onResendInvite, onDeactivate, onReactivate }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '1.1rem 1.25rem',
      marginBottom: '2rem',
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
    }}>
      {user.is_active && !user.has_logged_in && (
        <button className="btn btn-secondary btn-sm" onClick={onResendInvite}>
          שלח שוב הזמנה
        </button>
      )}
      {user.is_active && user.has_logged_in && (
        <button className="btn btn-secondary btn-sm" onClick={onSendPasswordReset}>
          שלח קישור איפוס סיסמה
        </button>
      )}
      {user.is_active && !isSelf && (
        <button
          type="button"
          onClick={onDeactivate}
          style={{
            padding: '0.4rem 0.85rem',
            background: '#fef2f2',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            borderRadius: '5px',
            fontSize: '0.82rem',
            cursor: 'pointer',
            fontFamily: 'Heebo, sans-serif',
          }}
        >
          השבת משתמש
        </button>
      )}
      {!user.is_active && (
        <button className="btn btn-secondary btn-sm" onClick={onReactivate}>
          הפעל מחדש
        </button>
      )}
    </div>
  );
}


// ---- Shared styles -------------------------------------------------------

const inputStyle = {
  padding: '0.55rem 0.75rem',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '0.9rem',
  fontFamily: 'Heebo, sans-serif',
  width: '100%',
  boxSizing: 'border-box',
};


export default UserProfile;
